import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { clearCredentials, setAdmin, setCredentials } from "../auth/adminAuthSlice";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.gubcpc.com";
const ADMIN_CSRF_COOKIE_NAME =
  process.env.REACT_APP_ADMIN_CSRF_COOKIE_NAME || "admin_csrf_token";

const getCookieValue = (name) => {
  if (typeof document === "undefined" || !document.cookie) return "";
  const target = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));
  if (!target) return "";
  return decodeURIComponent(target.slice(name.length + 1));
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const token = getState()?.adminAuth?.accessToken;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }

    const csrfToken = getCookieValue(ADMIN_CSRF_COOKIE_NAME);
    if (csrfToken) {
      headers.set("x-csrf-token", csrfToken);
    }
    return headers;
  },
});

const getUrl = (args) => (typeof args === "string" ? args : args?.url || "");
let refreshPromise = null;

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  const url = getUrl(args);
  const isAdminRequest = url.startsWith("/admin");
  const isAuthRoute = ["/admin/login", "/admin/refresh-token", "/admin/logout"].includes(url);

  if (result?.error?.status === 401 && isAdminRequest && !isAuthRoute) {
    if (!refreshPromise) {
      refreshPromise = rawBaseQuery(
        { url: "/admin/refresh-token", method: "POST" },
        api,
        extraOptions
      ).finally(() => {
        refreshPromise = null;
      });
    }

    const refreshResult = await refreshPromise;

    if (refreshResult?.data?.token) {
      api.dispatch(
        setCredentials({
          token: refreshResult.data.token,
          admin: refreshResult.data.admin,
        })
      );
      result = await rawBaseQuery(args, api, extraOptions);
    } else {
      api.dispatch(clearCredentials());
    }
  }

  return result;
};

export const appApi = createApi({
  reducerPath: "appApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "AdminAuth",
    "PublicEvents",
    "PublicAlumni",
    "PublicMentors",
    "PublicHall",
    "PublicCommittee",
    "PublicIupc",
    "PublicIupcTeams",
    "AdminEvents",
    "AdminIupc",
    "AdminAlumni",
    "AdminCommittee",
    "AdminHall",
    "AdminRecruitment",
    "PublicContests",
    "AdminContests",
    "AdminContestRegistrations",
  ],
  endpoints: (builder) => ({
    getEvents: builder.query({
      query: () => "/student/events",
      transformResponse: (res) => res?.data || [],
      providesTags: ["PublicEvents"],
    }),
    getAlumni: builder.query({
      query: () => "/student/alumni",
      transformResponse: (res) => res?.data || [],
      providesTags: ["PublicAlumni"],
    }),
    getMentors: builder.query({
      query: () => "/student/mentors",
      transformResponse: (res) => res?.data || [],
      providesTags: ["PublicMentors"],
    }),
    getHallOfShame: builder.query({
      query: () => "/student/hall-of-shame",
      transformResponse: (res) => res?.data || [],
      providesTags: ["PublicHall"],
    }),
    getCommittee: builder.query({
      query: () => "/student/committee",
      transformResponse: (res) => res?.data || [],
      providesTags: ["PublicCommittee"],
    }),
    getUpcomingIupc: builder.query({
      query: () => "/student/all-upcoming-iupc",
      transformResponse: (res) => res?.data || [],
      providesTags: ["PublicIupc"],
    }),
    getPastIupc: builder.query({
      query: () => "/student/all-past-iupc",
      transformResponse: (res) => res?.data || [],
      providesTags: ["PublicIupc"],
    }),
    getIupcTeams: builder.query({
      query: (iupcId) => `/student/teams-by-iupc/${iupcId}`,
      transformResponse: (res) => res?.data || [],
      providesTags: (_res, _err, iupcId) => [{ type: "PublicIupcTeams", id: iupcId }],
    }),

    loginAdmin: builder.mutation({
      query: (body) => ({ url: "/admin/login", method: "POST", body }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setCredentials({ token: data?.token, admin: data?.admin }));
        } catch {}
      },
      invalidatesTags: ["AdminAuth"],
    }),
    logoutAdmin: builder.mutation({
      query: () => ({ url: "/admin/logout", method: "POST" }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {}
        dispatch(clearCredentials());
      },
    }),
    getAdminMe: builder.query({
      query: () => "/admin/me",
      transformResponse: (res) => res?.admin || null,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setAdmin(data));
        } catch {}
      },
      providesTags: ["AdminAuth"],
    }),
    changeAdminPassword: builder.mutation({
      query: (body) => ({
        url: "/admin/change-password",
        method: "PUT",
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.token) {
            dispatch(
              setCredentials({
                token: data.token,
                admin: data.admin,
              })
            );
          }
        } catch {}
      },
    }),
    createAdminAccount: builder.mutation({
      query: (body) => ({
        url: "/admin/create-admin",
        method: "POST",
        body,
      }),
    }),

    getAdminEvents: builder.query({
      query: () => "/admin/events",
      transformResponse: (res) => res?.events || [],
      providesTags: ["AdminEvents"],
    }),
    createEvent: builder.mutation({
      query: (body) => ({ url: "/admin/events", method: "POST", body }),
      invalidatesTags: ["AdminEvents", "PublicEvents"],
    }),
    deleteEvent: builder.mutation({
      query: (id) => ({ url: `/admin/events/${id}`, method: "DELETE" }),
      invalidatesTags: ["AdminEvents", "PublicEvents"],
    }),

    getAdminIupc: builder.query({
      query: () => "/admin/iupc",
      transformResponse: (res) => res?.iupcs || [],
      providesTags: ["AdminIupc"],
    }),
    createIupc: builder.mutation({
      query: (body) => ({ url: "/admin/iupc", method: "POST", body }),
      invalidatesTags: ["AdminIupc", "PublicIupc"],
    }),
    deleteIupc: builder.mutation({
      query: (id) => ({ url: `/admin/iupc/${id}`, method: "DELETE" }),
      invalidatesTags: ["AdminIupc", "PublicIupc"],
    }),
    createIupcTeam: builder.mutation({
      query: ({ iupcId, ...body }) => ({
        url: `/admin/iupc/${iupcId}/teams`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        "AdminIupc",
        "PublicIupc",
        { type: "PublicIupcTeams", id: arg.iupcId },
      ],
    }),

    getAdminAlumni: builder.query({
      query: () => "/admin/alumni",
      transformResponse: (res) => res?.alumni || [],
      providesTags: ["AdminAlumni"],
    }),
    createAlumni: builder.mutation({
      query: (body) => ({ url: "/admin/alumni", method: "POST", body }),
      invalidatesTags: ["AdminAlumni", "PublicAlumni"],
    }),
    deleteAlumni: builder.mutation({
      query: (id) => ({ url: `/admin/alumni/${id}`, method: "DELETE" }),
      invalidatesTags: ["AdminAlumni", "PublicAlumni"],
    }),

    getAdminCommitteeYears: builder.query({
      query: () => "/admin/committee/years",
      transformResponse: (res) => res?.years || [],
      providesTags: ["AdminCommittee"],
    }),
    getAdminCommitteePeople: builder.query({
      query: () => "/admin/committee/people",
      transformResponse: (res) => res?.people || [],
      providesTags: ["AdminCommittee"],
    }),
    createCommitteePerson: builder.mutation({
      query: (body) => ({
        url: "/admin/committee/people",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminCommittee", "PublicCommittee"],
    }),
    updateCommitteePerson: builder.mutation({
      query: ({ personId, ...body }) => ({
        url: `/admin/committee/people/${personId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["AdminCommittee", "PublicCommittee"],
    }),
    createCommitteeYear: builder.mutation({
      query: (body) => ({
        url: "/admin/committee/years",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminCommittee", "PublicCommittee"],
    }),
    deleteCommitteeYear: builder.mutation({
      query: (yearId) => ({
        url: `/admin/committee/years/${yearId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdminCommittee", "PublicCommittee"],
    }),
    addCommitteeMember: builder.mutation({
      query: ({ yearId, ...body }) => ({
        url: `/admin/committee/years/${yearId}/members`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminCommittee", "PublicCommittee"],
    }),
    updateCommitteeMember: builder.mutation({
      query: ({ memberId, ...body }) => ({
        url: `/admin/committee/members/${memberId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["AdminCommittee", "PublicCommittee"],
    }),
    deleteCommitteeMember: builder.mutation({
      query: (memberId) => ({
        url: `/admin/committee/members/${memberId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdminCommittee", "PublicCommittee"],
    }),

    getAdminHall: builder.query({
      query: () => "/admin/hall-of-shame",
      transformResponse: (res) => res?.records || [],
      providesTags: ["AdminHall"],
    }),
    createHallRecord: builder.mutation({
      query: (body) => ({
        url: "/admin/hall-of-shame",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminHall", "PublicHall"],
    }),

    uploadImage: builder.mutation({
      query: (body) => ({ url: "/admin/upload-image", method: "POST", body }),
      transformResponse: (res) => res?.data?.data?.url || res?.data?.url || "",
    }),

    getAdminRecruitmentApplications: builder.query({
      query: ({
        status = "all",
        position = "",
        unshortlisted = false,
        inviteStatus = "all",
        noInterviewTime = false,
        page = 1,
        limit = 20,
      } = {}) => {
        const params = new URLSearchParams();
        params.set("status", status);
        if (position) params.set("position", position);
        if (unshortlisted) params.set("unshortlisted", "true");
        if (inviteStatus && inviteStatus !== "all") {
          params.set("inviteStatus", inviteStatus);
        }
        if (noInterviewTime) {
          params.set("noInterviewTime", "true");
        }
        params.set("page", String(page));
        params.set("limit", String(limit));
        return `/admin/recruitment/applications?${params.toString()}`;
      },
      transformResponse: (res) => ({
        applications: res?.applications || [],
        page: Number(res?.page || 1),
        limit: Number(res?.limit || 20),
        total: Number(res?.total || 0),
        totalPages: Number(res?.totalPages || 1),
      }),
      providesTags: ["AdminRecruitment"],
    }),
    getAdminRecruitmentPositions: builder.query({
      query: () => "/admin/recruitment/positions",
      transformResponse: (res) => res?.positions || [],
      providesTags: ["AdminRecruitment"],
    }),
    getAdminRecruitmentStats: builder.query({
      query: ({
        status = "all",
        position = "",
        unshortlisted = false,
      } = {}) => {
        const params = new URLSearchParams();
        params.set("status", status);
        if (position) params.set("position", position);
        if (unshortlisted) params.set("unshortlisted", "true");
        return `/admin/recruitment/stats?${params.toString()}`;
      },
      transformResponse: (res) => ({
        totalApplications: Number(res?.totalApplications || 0),
        byPost: Array.isArray(res?.byPost) ? res.byPost : [],
        byDepartment: Array.isArray(res?.byDepartment) ? res.byDepartment : [],
        byBatch: Array.isArray(res?.byBatch) ? res.byBatch : [],
      }),
      providesTags: ["AdminRecruitment"],
    }),
    shortlistRecruitmentApplication: builder.mutation({
      query: (id) => ({
        url: `/admin/recruitment/applications/${id}/shortlist`,
        method: "PUT",
      }),
      invalidatesTags: ["AdminRecruitment"],
    }),

    // ------------------------------------------------------------------
    // Dynamic Contest Registration System
    // ------------------------------------------------------------------
    getPublicContests: builder.query({
      query: () => "/student/contests",
      transformResponse: (res) => res?.contests || [],
      providesTags: ["PublicContests"],
    }),
    getPublicContestBySlug: builder.query({
      query: (slug) => `/student/contests/${slug}`,
      transformResponse: (res) => res?.contest || null,
      providesTags: (_r, _e, slug) => [{ type: "PublicContests", id: slug }],
    }),

    getAdminContests: builder.query({
      query: ({ search = "", status = "", type = "" } = {}) => {
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        if (status) params.set("status", status);
        if (type) params.set("type", type);
        return `/admin/contests?${params.toString()}`;
      },
      transformResponse: (res) => res?.contests || [],
      providesTags: ["AdminContests"],
    }),
    getAdminContest: builder.query({
      query: (id) => `/admin/contests/${id}`,
      transformResponse: (res) => res?.contest || null,
      providesTags: (_r, _e, id) => [{ type: "AdminContests", id }],
    }),
    createContest: builder.mutation({
      query: (body) => ({ url: "/admin/contests", method: "POST", body }),
      invalidatesTags: ["AdminContests", "PublicContests"],
    }),
    updateContest: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/admin/contests/${id}`, method: "PATCH", body }),
      invalidatesTags: (_r, _e, arg) => [
        "AdminContests",
        "PublicContests",
        { type: "AdminContests", id: arg.id },
      ],
    }),
    regenerateContestApiKey: builder.mutation({
      query: (id) => ({ url: `/admin/contests/${id}/regenerate-key`, method: "POST" }),
      invalidatesTags: (_r, _e, id) => [{ type: "AdminContests", id }],
    }),
    deleteContest: builder.mutation({
      query: ({ id, force = false }) => ({
        url: `/admin/contests/${id}${force ? "?force=true" : ""}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdminContests", "PublicContests"],
    }),

    getAdminContestRegistrations: builder.query({
      query: ({ contestId, search = "", status = "", reference = "", sortBy = "createdAt", sortOrder = "DESC", page = 1, limit = 20 }) => {
        const params = new URLSearchParams({ sortBy, sortOrder, page, limit });
        if (search) params.set("search", search);
        if (status) params.set("status", status);
        if (reference) params.set("reference", reference);
        return `/admin/contests/${contestId}/registrations?${params.toString()}`;
      },
      providesTags: (_r, _e, arg) => [{ type: "AdminContestRegistrations", id: arg.contestId }],
    }),
    createManualRegistration: builder.mutation({
      query: ({ contestId, ...body }) => ({
        url: `/admin/contests/${contestId}/registrations`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "AdminContestRegistrations", id: arg.contestId },
        { type: "AdminContests", id: arg.contestId },
        "AdminContests",
      ],
    }),
    updateContestRegistration: builder.mutation({
      query: ({ contestId, registrationId, ...body }) => ({
        url: `/admin/contests/${contestId}/registrations/${registrationId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: "AdminContestRegistrations", id: arg.contestId }],
    }),
    deleteContestRegistration: builder.mutation({
      query: ({ contestId, registrationId }) => ({
        url: `/admin/contests/${contestId}/registrations/${registrationId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "AdminContestRegistrations", id: arg.contestId },
        { type: "AdminContests", id: arg.contestId },
        "AdminContests",
      ],
    }),
  }),
});

export const {
  useGetEventsQuery,
  useGetAlumniQuery,
  useGetMentorsQuery,
  useGetHallOfShameQuery,
  useGetCommitteeQuery,
  useGetUpcomingIupcQuery,
  useGetPastIupcQuery,
  useGetIupcTeamsQuery,
  useLoginAdminMutation,
  useLogoutAdminMutation,
  useGetAdminMeQuery,
  useChangeAdminPasswordMutation,
  useCreateAdminAccountMutation,
  useGetAdminEventsQuery,
  useCreateEventMutation,
  useDeleteEventMutation,
  useGetAdminIupcQuery,
  useCreateIupcMutation,
  useDeleteIupcMutation,
  useCreateIupcTeamMutation,
  useGetAdminAlumniQuery,
  useCreateAlumniMutation,
  useDeleteAlumniMutation,
  useGetAdminCommitteeYearsQuery,
  useGetAdminCommitteePeopleQuery,
  useCreateCommitteePersonMutation,
  useUpdateCommitteePersonMutation,
  useCreateCommitteeYearMutation,
  useDeleteCommitteeYearMutation,
  useAddCommitteeMemberMutation,
  useUpdateCommitteeMemberMutation,
  useDeleteCommitteeMemberMutation,
  useGetAdminHallQuery,
  useCreateHallRecordMutation,
  useUploadImageMutation,
  useGetAdminRecruitmentApplicationsQuery,
  useGetAdminRecruitmentPositionsQuery,
  useGetAdminRecruitmentStatsQuery,
  useShortlistRecruitmentApplicationMutation,
  useGetPublicContestsQuery,
  useGetPublicContestBySlugQuery,
  useGetAdminContestsQuery,
  useGetAdminContestQuery,
  useCreateContestMutation,
  useUpdateContestMutation,
  useRegenerateContestApiKeyMutation,
  useDeleteContestMutation,
  useGetAdminContestRegistrationsQuery,
  useCreateManualRegistrationMutation,
  useUpdateContestRegistrationMutation,
  useDeleteContestRegistrationMutation,
} = appApi;
